/**
 * Formatting-insensitive address comparison (#36106).
 *
 * Dependency-free on purpose: exported as `@ourbranch/onboarding-tasks/address-comparison` so the
 * bind-requests-to-accounts lambda (which keeps a Front ticket for non-Branch brands) can share the
 * exact comparison the `Address Mismatch Verification` internal task uses.
 */

/** Subset of AddressDetails sufficient to compare: street line, city, state, zip. */
export type ComparableAddress = {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

// The address-mismatch internal task compares the insured property address with the
// mailing address and each car's garaging address. In production ~55% of the task's
// firings were formatting noise (ZIP+4 vs ZIP5, "Lane" vs "Ln.", "North" vs "N"), so
// comparison runs on a canonical form instead of raw text. Not a USPS-grade
// standardizer — just the equivalences UW Ops saw as false positives.

/** USPS Publication 28 street-suffix abbreviations (common subset), keyed by every spelling. */
const STREET_SUFFIX_ABBREVIATIONS: Record<string, string> = {
  alley: 'aly',
  aly: 'aly',
  avenue: 'ave',
  av: 'ave',
  ave: 'ave',
  aven: 'ave',
  avn: 'ave',
  boulevard: 'blvd',
  blvd: 'blvd',
  boul: 'blvd',
  boulv: 'blvd',
  bypass: 'byp',
  byp: 'byp',
  circle: 'cir',
  cir: 'cir',
  circ: 'cir',
  crcl: 'cir',
  court: 'ct',
  ct: 'ct',
  crt: 'ct',
  cove: 'cv',
  cv: 'cv',
  creek: 'crk',
  crk: 'crk',
  crescent: 'cres',
  cres: 'cres',
  crossing: 'xing',
  xing: 'xing',
  drive: 'dr',
  dr: 'dr',
  drv: 'dr',
  driv: 'dr',
  expressway: 'expy',
  expy: 'expy',
  expwy: 'expy',
  extension: 'ext',
  ext: 'ext',
  freeway: 'fwy',
  fwy: 'fwy',
  highway: 'hwy',
  hwy: 'hwy',
  hiway: 'hwy',
  hway: 'hwy',
  hill: 'hl',
  hl: 'hl',
  hills: 'hls',
  hls: 'hls',
  junction: 'jct',
  jct: 'jct',
  lane: 'ln',
  ln: 'ln',
  la: 'ln',
  loop: 'loop',
  manor: 'mnr',
  mnr: 'mnr',
  parkway: 'pkwy',
  pkwy: 'pkwy',
  pky: 'pkwy',
  parkwy: 'pkwy',
  pass: 'pass',
  path: 'path',
  pike: 'pike',
  place: 'pl',
  pl: 'pl',
  plaza: 'plz',
  plz: 'plz',
  point: 'pt',
  pt: 'pt',
  ridge: 'rdg',
  rdg: 'rdg',
  road: 'rd',
  rd: 'rd',
  route: 'rte',
  rte: 'rte',
  row: 'row',
  run: 'run',
  square: 'sq',
  sq: 'sq',
  sqr: 'sq',
  station: 'sta',
  sta: 'sta',
  street: 'st',
  st: 'st',
  str: 'st',
  strt: 'st',
  terrace: 'ter',
  ter: 'ter',
  terr: 'ter',
  trace: 'trce',
  trce: 'trce',
  trail: 'trl',
  trl: 'trl',
  tr: 'trl',
  turnpike: 'tpke',
  tpke: 'tpke',
  valley: 'vly',
  vly: 'vly',
  view: 'vw',
  vw: 'vw',
  village: 'vlg',
  vlg: 'vlg',
  walk: 'walk',
  way: 'way',
  wy: 'way'
};

/** USPS directional abbreviations, keyed by every spelling (including the unpunctuated compound forms). */
const DIRECTIONAL_ABBREVIATIONS: Record<string, string> = {
  north: 'n',
  n: 'n',
  south: 's',
  s: 's',
  east: 'e',
  e: 'e',
  west: 'w',
  w: 'w',
  northeast: 'ne',
  ne: 'ne',
  northwest: 'nw',
  nw: 'nw',
  southeast: 'se',
  se: 'se',
  southwest: 'sw',
  sw: 'sw'
};

/**
 * Secondary-unit designators, each mapped to a canonical token. Only true synonyms share a
 * token ("Apt 4", "Apartment 4", "Unit 4", "#4", "Ste 4" and "Suite 4" are the same place);
 * Bldg/Fl/Lot/Rm/Spc/Trlr/Dept/Ofc stay distinct from each other and from Apt so "Bldg 4"
 * vs "Apt 4" is still a real mismatch (devlo on #36174). Bare "no"/"num" are deliberately
 * not here — "no" is an ordinary word in street lines.
 */
const UNIT_DESIGNATOR_TOKENS: Record<string, string> = {
  apt: 'unit',
  apartment: 'unit',
  unit: 'unit',
  ste: 'unit',
  suite: 'unit',
  bldg: 'bldg',
  building: 'bldg',
  fl: 'fl',
  floor: 'fl',
  lot: 'lot',
  rm: 'rm',
  room: 'rm',
  spc: 'spc',
  space: 'spc',
  trlr: 'trlr',
  trailer: 'trlr',
  dept: 'dept',
  department: 'dept',
  ofc: 'ofc',
  office: 'ofc'
};

/**
 * Canonical street line: lowercase; apostrophes and dotted initials joined; other punctuation
 * removed; `#` treated as a unit designator (and collapsed when redundant, "Apt #4");
 * suffixes/directionals abbreviated; unit designators unified per type.
 */
function canonicalStreetLine(street: string): string {
  const tokens = street
    .toLowerCase()
    // Apostrophes join rather than split: "O'Brien" ≡ "OBrien".
    .replace(/'/g, '')
    // Dotted initials join: "N.E." ≡ "NE", "S.W." ≡ "SW" (a period between two single letters).
    .replace(/\b([a-z])\.(?=[a-z]\b)/g, '$1')
    .replace(/\b([a-z])\.(?=[a-z]\b)/g, '$1')
    .replace(/#/g, ' unit ')
    .replace(/[.,"()]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(
      (token) =>
        UNIT_DESIGNATOR_TOKENS[token] || DIRECTIONAL_ABBREVIATIONS[token] || STREET_SUFFIX_ABBREVIATIONS[token] || token
    );
  // "Apt #4" → "unit unit 4": a unit marker directly after another says nothing new.
  return tokens.filter((token, i) => !(token === 'unit' && tokens[i - 1] === 'unit')).join(' ');
}

/**
 * Normalize an address for formatting-insensitive equality (#36106): the street line
 * is canonicalized (see `canonicalStreetLine`), city/state are lowercased with
 * punctuation removed, and only the 5-digit ZIP is kept. Returns '' when no part of
 * the address is present, so callers can treat "missing" as "cannot compare".
 *
 * Prefer this over `normalizeAddress` for any mismatch check — the older helper only
 * strips whitespace, so ZIP+4, "Ln." vs "Lane" and "N" vs "North" all mismatch.
 */
export function normalizeAddressForComparison(addr?: ComparableAddress | null): string {
  if (!addr) {
    return '';
  }
  const street = canonicalStreetLine(addr.address || '');
  const city = (addr.city || '')
    .toLowerCase()
    .replace(/[.,'"]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const state = (addr.state || '').toLowerCase().trim();
  const zip5 = (addr.zip || '').replace(/\D/g, '').slice(0, 5);
  if (!street && !city && !state && !zip5) {
    return '';
  }
  return [street, city, state, zip5].join('|');
}
